import {
	type BranchSalesChannelCategoryGroupQopFilterInput,
	useBranchSalesChannelCategoryGroupsQuery,
} from "@/generated/graphql";
import type { PaginationState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

export interface BranchSalesChannelCategoryGroupRow {
	branchSalesChannelCategoryGroupID: string;
	branchID: string;
	branchCode: string;
	branchName: string;
	salesChannelID: string;
	salesChannelCategoryGroupID: string;
	salesChannelCategoryGroupName: string;
}

// Server-paged branch bindings, always scoped to one category group.
export const useBranchSalesChannelCategoryGroupList = (
	groupID: string | undefined,
) => {
	const [paginationState, setPaginationState] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});
	const [filtersState, setFiltersState] = useState<Record<string, unknown>>({});

	const options = useMemo(
		() => ({
			variables: {
				qop: {
					pagination: {
						page: paginationState.pageIndex + 1,
						pageSize: paginationState.pageSize,
						withCount: true,
					},
					filters: {
						...filtersState,
						salesChannelCategoryGroupID: groupID ?? null,
					} as BranchSalesChannelCategoryGroupQopFilterInput,
				},
			},
			pause: !groupID,
			requestPolicy: "network-only" as const,
			context: { suspense: false },
		}),
		[
			paginationState.pageIndex,
			paginationState.pageSize,
			filtersState,
			groupID,
		],
	);

	const [{ data, error, fetching }, fetch] =
		useBranchSalesChannelCategoryGroupsQuery(options);

	const load = useCallback(
		() => fetch({ requestPolicy: "network-only" }),
		[fetch],
	);

	const setPagination = useCallback(
		(next: PaginationState) => setPaginationState(next),
		[],
	);

	const setFilters = useCallback((next: Record<string, unknown>) => {
		setFiltersState(next);
		setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
	}, []);

	return {
		items: data?.branchSalesChannelCategoryGroups.items ?? [],
		rowCount: data?.branchSalesChannelCategoryGroups.pagination.totalRows ?? 0,
		fetching,
		error,
		load,
		paginationState,
		setPagination,
		filtersState,
		setFilters,
	};
};
